import type { FC } from 'react';
import React, { memo } from 'react';
import { Chip, Stack, styled, Typography } from '@mui/material';
import type { LineItem } from '../ServiceProviderReceiptBatchForm';
import { QuickResidentsSelectionLineItemForm } from './QuickResidentsSelectionLineItemForm';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';

interface QuickResidentsSelectionLineItemsProps {
  lineItems: LineItem[];
  addLineItem: (lineItem: LineItem) => void;
  removeLineItem: (_internalId: string) => void;
}

export const QuickResidentsSelectionLineItems: FC<QuickResidentsSelectionLineItemsProps> =
  memo(({ lineItems, addLineItem, removeLineItem }) => {
    const { t } = useTranslation();
    const { fCurrencyCents } = useFormatting();

    return (
      <Stack
        sx={{
          gap: 4,
        }}
      >
        <Stack
          sx={{
            gap: 1.5,
          }}
        >
          <Stack
            sx={{
              gap: 0.5,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
              }}
            >
              {t(
                'receipts.batch.form.resident-quick-selection.line-items.title'
              )}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
              }}
            >
              {t(
                'receipts.batch.form.resident-quick-selection.line-items.subtitle'
              )}
            </Typography>
          </Stack>

          <QuickResidentsSelectionLineItemForm
            lineItems={lineItems}
            addLineItem={addLineItem}
          />
        </Stack>
        <ScrollableServicesList
          sx={{ flexWrap: 'wrap', alignItems: 'flex-start', gap: 1 }}
          direction="row"
        >
          {lineItems.map((lineItem) => (
            <Chip
              key={lineItem._internalId}
              label={
                lineItem.amount
                  ? `${lineItem.name} (${fCurrencyCents(lineItem.amount ?? 0)})`
                  : lineItem.name
              }
              onDelete={() => removeLineItem(lineItem._internalId)}
              variant="filled"
              color="primary"
            />
          ))}
        </ScrollableServicesList>
      </Stack>
    );
  });

interface ScrollableServicesListProps {
  height?: string;
}

export const ScrollableServicesList = styled(
  Stack
)<ScrollableServicesListProps>(({ height = 180 }) => ({
  overflowY: 'scroll',
  maxHeight: height,
  '&::-webkit-scrollbar': {
    width: '0.3em',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,.1)',
  },
}));
